import { useCheckboxState } from '@vtex/admin-ui'
import { FormState, useWatch } from '@vtex/admin-ui-form'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { SubscriptionCycle, SubscriptionCycleOperators } from 'typings'
import { CollapsibleCheckbox } from '../../../../../CollapsibleCheckbox'
import { NumberInput } from '../../../../../NumberInput'
import { messages } from '../../messages'
import { styles } from '../../styles'
import { CycleSelector } from './CycleSelector'
import { RangeInput } from './RangeInput'

type Props = {
  name: string
  form: FormState
  disabled?: boolean
}

const EMPTY_FILTER: Partial<SubscriptionCycle> = Object.freeze({
  operator: SubscriptionCycleOperators.GREATER_THAN,
  value: undefined,
})

export function CycleFilter(props: Props) {
  const { name, form, disabled = false } = props

  const { cycle: cycleMessages } = messages.editor
  const { $t } = useIntl()

  const cycle: SubscriptionCycle | undefined = useWatch({
    form,
    name,
  })

  const checkboxState = useCheckboxState({
    initialValue: Boolean(cycle),
  })

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    form.setValue(name, event.target.checked ? { ...EMPTY_FILTER } : undefined)
    form.clearErrors(name)
  }

  const handleSelectChange = (event: React.FormEvent<HTMLSelectElement>) => {
    const operator = event.currentTarget.value

    if (operator == SubscriptionCycleOperators.RANGE) {
      form.setValue(`${name}.value`, undefined)
    } else {
      form.setValue(`${name}.min`, undefined)
      form.setValue(`${name}.max`, undefined)
    }
    form.clearErrors(name)
  }

  useEffect(() => {
    if (!cycle) checkboxState.setValue(false)
  }, [checkboxState, cycle])

  useEffect(() => {
    if (disabled) form.setValue(name, undefined)
  }, [disabled, name, form])

  const isRange = cycle?.operator === SubscriptionCycleOperators.RANGE

  return (
    <CollapsibleCheckbox
      label={$t(cycleMessages.label)}
      state={checkboxState}
      disabled={disabled}
      onChange={handleCheckboxChange}
      checked={Boolean(cycle)}
    >
      <div className={isRange ? styles.flexColumn : styles.flexRow}>
        <CycleSelector name={name} form={form} onChange={handleSelectChange} />
        {isRange ? (
          <RangeInput name={name} form={form} type="conjunction" />
        ) : (
          <NumberInput name={`${name}.value`} state={form} fractionDigits={0} />
        )}
      </div>
    </CollapsibleCheckbox>
  )
}
